<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>充值订单</h1>
        <p>微信支付流水、订单退款与异常订单清理</p>
      </div>
    </div>

    <!-- ── 指标 ───────────────────────────────────────────── -->
    <div class="stat-row">
      <div class="stat-card is-accent">
        <div class="stat-num is-rose">{{ toBean(statistics.totalAmount || 0) }}</div>
        <div class="stat-label">累计充值爱豆</div>
        <div class="stat-sub">¥{{ Number(statistics.totalAmount || 0).toFixed(2) }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ statistics.totalCount || 0 }}</div>
        <div class="stat-label">总订单数</div>
      </div>
      <div class="stat-card is-ok">
        <div class="stat-num is-ok">{{ toBean(statistics.todayAmount || 0) }}</div>
        <div class="stat-label">今日充值爱豆</div>
        <div class="stat-sub">¥{{ Number(statistics.todayAmount || 0).toFixed(2) }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ statistics.todayCount || 0 }}</div>
        <div class="stat-label">今日订单</div>
      </div>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-input
        v-model="listQuery.tradeNo"
        placeholder="搜索订单号"
        clearable
        prefix-icon="el-icon-search"
        class="search-input"
        @keyup.enter.native="handleFilter"
        @clear="handleFilter"
      />
      <el-input
        v-model="listQuery.username"
        placeholder="用户昵称"
        clearable
        class="nick-input"
        @keyup.enter.native="handleFilter"
        @clear="handleFilter"
      />
      <el-select v-model="listQuery.status" placeholder="全部状态" clearable class="status-select" @change="handleFilter">
        <el-option label="待支付" value="pending" />
        <el-option label="支付成功" value="success" />
        <el-option label="支付失败" value="failed" />
        <el-option label="退款中" value="refunding" />
        <el-option label="已退款" value="refunded" />
      </el-select>
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        value-format="yyyy-MM-dd"
        class="date-range"
        @change="handleFilter"
      />
      <el-button type="primary" class="primary-btn" @click="handleFilter">查询</el-button>
      <el-button class="ghost-btn" @click="resetFilter">重置</el-button>
      <div class="filter-tail">共 <b>{{ total }}</b> 笔</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="listLoading" :data="list" style="width: 100%">
        <el-table-column label="订单号" min-width="220">
          <template slot-scope="{ row }">
            <span class="tag is-mono">{{ row.tradeNo }}</span>
          </template>
        </el-table-column>
        <el-table-column label="用户" min-width="140">
          <template slot-scope="{ row }">
            <div class="cell-main">{{ row.userNickname || '—' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="金额" width="130">
          <template slot-scope="{ row }">
            <div class="bean-cell"><b>{{ toBean(row.amount) }}</b> 爱豆</div>
            <div class="cell-sub">¥{{ Number(row.amount || 0).toFixed(2) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="支付方式" width="130">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ channelText(row.payChannel) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="120">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="statusClass(row.status)">
              <i />{{ getStatusText(row.status) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="170">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ formatDate(row.createdAt) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <button type="button" class="act act-plain" @click="handleDetail(row)">详情</button>
              <button
                v-if="row.status === 'success'"
                type="button"
                class="act act-danger"
                @click="handleRefund(row)"
              >
                退款
              </button>
              <button
                v-if="row.status === 'pending'"
                type="button"
                class="act act-danger"
                @click="handleDelete(row)"
              >
                删除
              </button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          v-show="total > 0"
          background
          :current-page="listQuery.page"
          :page-size="listQuery.size"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <el-dialog title="订单详情" :visible.sync="detailDialogVisible" width="600px">
      <el-descriptions :column="1" border>
        <el-descriptions-item label="订单号">{{ currentOrder.tradeNo }}</el-descriptions-item>
        <el-descriptions-item label="平台单号">{{ currentOrder.transactionId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="用户">{{ currentOrder.userNickname }}</el-descriptions-item>
        <el-descriptions-item label="支付方式">{{ channelText(currentOrder.payChannel) }}</el-descriptions-item>
        <el-descriptions-item label="金额">
          {{ toBean(currentOrder.amount) }} 爱豆（¥{{ Number(currentOrder.amount || 0).toFixed(2) }}）
        </el-descriptions-item>
        <el-descriptions-item label="状态">
          <span class="status-pill is-solid" :class="statusClass(currentOrder.status)">
            <i />{{ getStatusText(currentOrder.status) }}
          </span>
        </el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDate(currentOrder.createdAt) }}</el-descriptions-item>
      </el-descriptions>
      <div slot="footer" class="dialog-footer">
        <el-button v-if="currentOrder.status === 'success'" type="danger" class="danger-btn" @click="handleRefund(currentOrder)">退款</el-button>
        <el-button v-if="currentOrder.status === 'pending'" type="danger" class="danger-btn" @click="handleDelete(currentOrder)">删除</el-button>
        <el-button class="ghost-btn" @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="退款" :visible.sync="refundDialogVisible" width="500px">
      <el-form :model="refundForm" label-width="100px">
        <el-form-item label="订单号">
          <el-input v-model="refundForm.tradeNo" disabled />
        </el-form-item>
        <el-form-item label="退款金额">
          <el-input-number v-model="refundForm.refundAmount" :min="0.01" :max="currentOrder.amount" :precision="2" />
          <div class="form-hint">约 {{ toBean(refundForm.refundAmount) }} 爱豆</div>
        </el-form-item>
        <el-form-item label="退款原因">
          <el-input v-model="refundForm.remark" type="textarea" :rows="3" placeholder="请输入退款原因" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button class="ghost-btn" @click="refundDialogVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" :loading="refundLoading" @click="confirmRefund">确认退款</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getOrderList, getOrderStatistics, refundOrder, deleteOrder } from '@/api/recharge'
import { toBean } from '@/utils/bean'

export default {
  name: 'RechargeOrder',
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      listQuery: {
        page: 1,
        size: 10,
        tradeNo: '',
        username: '',
        status: ''
      },
      dateRange: [],
      statistics: {},
      detailDialogVisible: false,
      refundDialogVisible: false,
      refundLoading: false,
      currentOrder: {},
      refundForm: {
        tradeNo: '',
        refundAmount: 0,
        remark: ''
      }
    }
  },
  created() {
    this.fetchData()
    this.fetchStatistics()
  },
  methods: {
    toBean(yuan) {
      return toBean(yuan)
    },
    fetchData() {
      this.listLoading = true
      const params = { ...this.listQuery }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0]
        params.endDate = this.dateRange[1]
      }
      getOrderList(params).then(res => {
        this.list = res.data.list || []
        this.total = res.data.total || 0
        this.listLoading = false
      })
    },
    fetchStatistics() {
      getOrderStatistics().then(res => {
        this.statistics = res.data || {}
      })
    },
    handleFilter() {
      this.listQuery.page = 1
      this.fetchData()
    },
    resetFilter() {
      this.listQuery.tradeNo = ''
      this.listQuery.username = ''
      this.listQuery.status = ''
      this.dateRange = []
      this.handleFilter()
    },
    statusClass(status) {
      const map = {
        pending: 's0',
        success: 's1',
        failed: 's2',
        refunding: 's0',
        refunded: 's2'
      }
      return map[status] || 's0'
    },
    handleSizeChange(val) {
      this.listQuery.size = val
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchData()
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString()
    },
    getStatusType(status) {
      const statusMap = {
        'pending': 'warning',
        'success': 'success',
        'failed': 'danger',
        'refunding': 'warning',
        'refunded': 'info'
      }
      return statusMap[status] || 'info'
    },
    getStatusText(status) {
      const statusMap = {
        'pending': '待支付',
        'success': '支付成功',
        'failed': '支付失败',
        'refunding': '退款中',
        'refunded': '已退款'
      }
      return statusMap[status] || status
    },
    /**
     * 支付通道。
     * 虚拟支付（2026-10 起的主通道）与旧微信支付**退款路径完全不同**：
     * 虚拟支付走 /xpay/refund_order，旧通道走微信 v3 退款接口，
     * 所以运营在退款前必须能一眼看出这一单是从哪条通道付的。
     * 加列之前创建的历史订单 payChannel 为空，按"—"展示。
     */
    channelText(channel) {
      const map = {
        'VIRTUAL': '小程序虚拟支付',
        'WECHAT': '微信支付',
        'ADMIN': '后台充值'
      }
      return map[channel] || '—'
    },
    handleDetail(row) {
      this.currentOrder = { ...row }
      this.detailDialogVisible = true
    },
    handleRefund(row) {
      this.currentOrder = { ...row }
      this.refundForm.tradeNo = row.tradeNo
      this.refundForm.refundAmount = row.amount
      this.refundForm.remark = ''
      this.refundDialogVisible = true
    },
    confirmRefund() {
      if (!this.refundForm.remark || this.refundForm.remark.trim() === '') {
        this.$message.warning('请输入退款原因')
        return
      }

      this.refundLoading = true
      refundOrder(this.refundForm).then(res => {
        this.refundLoading = false
        if (res.code === 200) {
          this.$message.success(res.message || '退款处理中')
          this.refundDialogVisible = false
          this.detailDialogVisible = false
          this.fetchData()
          this.fetchStatistics()
        } else {
          this.$message.error(res.message || '退款失败')
        }
      }).catch(() => {
        this.refundLoading = false
        this.$message.error('退款失败')
      })
    },
    handleDelete(row) {
      this.$confirm(`确定要删除订单 ${row.tradeNo} 吗?`, '提示', {
        type: 'warning'
      }).then(() => {
        deleteOrder(row.tradeNo).then(res => {
          if (res.code === 200) {
            this.$message.success('删除成功')
            this.fetchData()
            this.fetchStatistics()
          } else {
            this.$message.error(res.message || '删除失败')
          }
        }).catch(() => {
          this.$message.error('删除失败')
        })
      }).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.nick-input {
  width: 150px;
}

.status-select {
  width: 130px;
}

.date-range {
  width: 260px;
}

.bean-cell {
  font-size: 13px;
  color: var(--ink-900);
}

.bean-cell b {
  font-size: 15px;
  font-weight: 600;
  color: var(--rose-600);
}

.form-hint {
  font-size: 12px;
  color: var(--ink-400);
  margin-top: 4px;
}

.danger-btn {
  color: #fff;
  background: var(--rose-600);
  border-color: var(--rose-600);
}

.danger-btn:hover,
.danger-btn:focus {
  background: var(--rose-500);
  border-color: var(--rose-500);
  color: #fff;
}
</style>
